/**
 * PhotoRenderStatus - Unified status display for photo renders
 * 
 * Shows loading spinner, progress bar, and status text over a black background.
 * Used by both main image renders (ViewManager) and preview renders (PreviewBox).
 * 
 * Features:
 * - Customizable status text
 * - HTML/CSS based layout that auto-resizes
 * - Support for split view positioning
 * - Persistent - can be reused across view mode changes
 * - Delayed display to prevent flashing on fast renders
 */

export class PhotoRenderStatus {
  /**
   * @param {HTMLElement} container - Container element to attach status overlay to
   * @param {Object} options - Configuration options
   * @param {string} options.text - Status text to display (default: "Rendering...")
   */
  constructor(container, options = {}) {
    this.container = container;
    this.text = options.text || 'Rendering...';
    
    // DOM elements
    this.overlay = null;
    this.spinner = null;
    this.progressBar = null;
    this.progressFill = null;
    
    // State
    this.isVisible = false;
    this.spinnerTimeout = null;
    this.currentStep = 0;
    this.totalSteps = 0;
    
    // Split view state
    this.splitPosition = null; // null = normal mode, number = split mode percentage
  }
  
  /**
   * Create the DOM elements (only creates once, then reuses)
   */
  create() {
    if (this.overlay) return; // Already created
    if (!this.container) return;
    
    // Create main overlay container
    this.overlay = document.createElement('div');
    this.overlay.className = 'photo-render-status absolute inset-0 bg-black/70 flex flex-col items-center justify-center z-10';
    this.overlay.style.display = 'none';
    
    // Create spinner container
    this.spinner = document.createElement('div');
    this.spinner.className = 'flex flex-col items-center justify-center text-white';
    this.spinner.innerHTML = `
      <svg class="animate-spin h-12 w-12 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <p class="text-center status-text">${this.text}</p>
    `;
    
    this.overlay.appendChild(this.spinner);
    
    // Create progress bar
    this.progressBar = document.createElement('div');
    this.progressBar.className = 'absolute bottom-0 left-0 right-0 bg-gray-900/80 p-3 z-10';
    this.progressBar.style.display = 'none';
    this.progressBar.innerHTML = '<div class="w-full h-2 bg-gray-700 rounded-full overflow-hidden"><div class="progress-fill h-full bg-blue-500 transition-all duration-300 relative overflow-hidden" style="width: 0%"><div class="shimmer-effect"></div></div></div>';
    
    this.progressFill = this.progressBar.querySelector('.progress-fill');
    
    // Ensure container is positioned for absolute children
    const currentPosition = window.getComputedStyle(this.container).position;
    if (currentPosition === 'static') {
      this.container.style.position = 'relative';
    }
    
    // Append to container
    this.container.appendChild(this.overlay);
    this.container.appendChild(this.progressBar);
  }
  
  /**
   * Update the status text
   * @param {string} text - New status text
   */
  setText(text) {
    this.text = text;
    if (this.spinner) {
      const textElement = this.spinner.querySelector('.status-text');
      if (textElement) {
        textElement.textContent = text;
      }
    }
  }
  
  /**
   * Show spinner with optional delay to prevent flashing
   * @param {number} delay - Delay in milliseconds (0 = immediate)
   */
  showSpinner(delay = 100) {
    if (!this.overlay) {
      this.create();
    }
    
    this.clearSpinnerTimeout();
    
    if (delay > 0) {
      this.spinnerTimeout = setTimeout(() => {
        this._showSpinnerImmediate();
        this.spinnerTimeout = null;
      }, delay);
    } else {
      this._showSpinnerImmediate();
    }
  }
  
  /**
   * Internal: Show spinner immediately
   */
  _showSpinnerImmediate() {
    if (this.overlay) {
      this.overlay.style.display = 'flex';
      this.isVisible = true;
    }
    
    // Also show progress bar at 0%
    if (this.progressBar) {
      this.progressBar.style.display = '';
      this.progressBar.style.opacity = '1';
    }
  }
  
  /**
   * Hide spinner
   */
  hideSpinner() {
    this.clearSpinnerTimeout();
    
    if (this.overlay) {
      this.overlay.style.display = 'none';
      this.isVisible = false;
    }
  }
  
  /**
   * Update progress bar
   * @param {number} currentStep - Current step number
   * @param {number} totalSteps - Total steps
   */
  updateProgress(currentStep, totalSteps) {
    this.currentStep = currentStep;
    this.totalSteps = totalSteps;
    
    if (!this.progressFill) return;
    
    const percentage = Math.min(100, (currentStep / totalSteps) * 100);
    this.progressFill.style.width = `${percentage}%`;
    
    // Ensure progress bar is visible
    if (this.progressBar && this.progressBar.style.display === 'none') {
      this.progressBar.style.display = '';
      this.progressBar.style.opacity = '1';
    }
  }
  
  /**
   * Reset progress bar to 0%
   */
  resetProgress() {
    this.currentStep = 0;
    if (this.progressFill) {
      this.progressFill.style.width = '0%';
    }
  }
  
  /**
   * Hide progress bar with fade out
   */
  hideProgressBar() {
    if (!this.progressBar) return;
    
    this.progressBar.style.transition = 'opacity 0.3s ease-out';
    this.progressBar.style.opacity = '0';
    
    setTimeout(() => {
      if (this.progressBar) {
        this.progressBar.style.display = 'none';
        this.progressBar.style.opacity = '1'; // Reset for next time
      }
    }, 300);
  }
  
  /**
   * Show error state
   * @param {string} message - Error message to display
   */
  showError(message = 'Failed to render photo') {
    this.clearSpinnerTimeout();
    
    if (!this.overlay) {
      this.create();
    }
    
    if (this.spinner) {
      this.spinner.innerHTML = `
        <svg class="w-16 h-16 mb-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <p class="text-center text-red-400">${message}</p>
      `;
    }
    
    if (this.overlay) {
      this.overlay.style.display = 'flex';
      this.isVisible = true;
    }
    
    // Hide progress bar
    if (this.progressBar) {
      this.progressBar.style.display = 'none';
    }
    
    // Auto-hide error after 3 seconds
    setTimeout(() => {
      this.hideSpinner();
    }, 3000);
  }
  
  /**
   * Set positioning for split view mode (uses CSS variable from parent)
   * @param {number} leftPercentage - Percentage from left (0-100) where the split divider is
   */
  setSplitViewPosition(leftPercentage) {
    this.splitPosition = leftPercentage;
    
    if (!this.overlay || !this.progressBar) return;
    
    // Both overlay and progress bar should only cover the right (edited) portion
    // Uses CSS variable for responsive positioning
    this.overlay.style.left = 'var(--split-position, 0)';
    this.overlay.style.right = '0';
    this.overlay.style.width = 'auto';
    
    this.progressBar.style.left = 'var(--split-position, 0)';
    this.progressBar.style.right = '0';
    this.progressBar.style.width = 'auto';
  }
  
  /**
   * Reset to normal full-width positioning
   */
  resetPosition() {
    this.splitPosition = null;
    
    if (!this.overlay || !this.progressBar) return;
    
    this.overlay.style.left = '';
    this.overlay.style.right = '';
    this.overlay.style.width = '';
    
    this.progressBar.style.left = '';
    this.progressBar.style.right = '';
    this.progressBar.style.width = '';
  }
  
  /**
   * Clear spinner timeout
   */
  clearSpinnerTimeout() {
    if (this.spinnerTimeout) {
      clearTimeout(this.spinnerTimeout);
      this.spinnerTimeout = null;
    }
  }
  
  /**
   * Check if spinner is visible
   * @returns {boolean}
   */
  isSpinnerVisible() {
    return this.isVisible;
  }
  
  /**
   * Destroy and clean up
   */
  destroy() {
    this.clearSpinnerTimeout();
    
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
    
    if (this.progressBar) {
      this.progressBar.remove();
      this.progressBar = null;
    }
    
    this.spinner = null;
    this.progressFill = null;
    this.isVisible = false;
  }
}
